import type { I18NMessages } from '@atlaskit/intl-messages-provider/common/types';

const AVAILABLE_LOCALES = [
	'cs',
	'da',
	'de',
	'en',
	'en_GB',
	'en_ZZ',
	'es',
	'fi',
	'fr',
	'hr',
	'hu',
	'it',
	'ja',
	'ko',
	'nb',
	'nl',
	'pl',
	'pt_BR',
	'ru',
	'sl',
	'sr_RS',
	'sr_YR',
	'sv',
	'th',
	'tr',
	'uk',
	'vi',
	'zh',
	'zh_TW',
] as const;

type AvailableLocale = (typeof AVAILABLE_LOCALES)[number];

const AVAILABLE_LOCALES_SET = new Set<string>(AVAILABLE_LOCALES);

const isAvailableLocale = (locale: string): locale is AvailableLocale =>
	AVAILABLE_LOCALES_SET.has(locale);

const importLocaleMessages = async (locale: AvailableLocale) => {
	switch (locale) {
		case 'cs':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/cs" */ './cs'
			);
		case 'da':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/da" */ './da'
			);
		case 'de':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/de" */ './de'
			);
		case 'en':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/en" */ './en'
			);
		case 'en_GB':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/en_GB" */ './en_GB'
			);
		case 'en_ZZ':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/en_ZZ" */ './en_ZZ'
			);
		case 'es':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/es" */ './es'
			);
		case 'fi':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/fi" */ './fi'
			);
		case 'fr':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/fr" */ './fr'
			);
		case 'hr':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/hr" */ './hr'
			);
		case 'hu':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/hu" */ './hu'
			);
		case 'it':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/it" */ './it'
			);
		case 'ja':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/ja" */ './ja'
			);
		case 'ko':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/ko" */ './ko'
			);
		case 'nb':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/nb" */ './nb'
			);
		case 'nl':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/nl" */ './nl'
			);
		case 'pl':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/pl" */ './pl'
			);
		case 'pt_BR':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/pt_BR" */ './pt_BR'
			);
		case 'ru':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/ru" */ './ru'
			);
		case 'sl':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/sl" */ './sl'
			);
		case 'sr_RS':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/sr_RS" */ './sr_RS'
			);
		case 'sr_YR':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/sr_YR" */ './sr_YR'
			);
		case 'sv':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/sv" */ './sv'
			);
		case 'th':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/th" */ './th'
			);
		case 'tr':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/tr" */ './tr'
			);
		case 'uk':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/uk" */ './uk'
			);
		case 'vi':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/vi" */ './vi'
			);
		case 'zh':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/zh" */ './zh'
			);
		case 'zh_TW':
			return import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-intro-animation/locale/zh_TW" */ './zh_TW'
			);
		default:
			throw new Error(`Unsupported locale: ${locale}`);
	}
};

export const fetchMessagesForLocale = async (locale: string): Promise<I18NMessages | undefined> => {
	const normalizedLocale = locale.replace(/-/g, '_');
	const parentLocale = locale.split(/[-_]/)[0];

	if (isAvailableLocale(normalizedLocale)) {
		try {
			const messages = await importLocaleMessages(normalizedLocale);
			return messages.default;
		} catch {
			// ignore
		}
	}

	if (isAvailableLocale(parentLocale)) {
		try {
			const messages = await importLocaleMessages(parentLocale);
			return messages.default;
		} catch {
			// ignore
		}
	}
};
